import React from 'react';
import './AILoader.css';

interface AILoaderProps {
    message?: string;
    subMessage?: string;
    variant?: 'default' | 'medical' | 'analysis' | 'processing';
    size?: 'small' | 'medium' | 'large';
    overlay?: boolean;
    className?: string;
}

const AILoader: React.FC<AILoaderProps> = ({
    message = 'Generating',
    subMessage = 'Your intelligent report is being crafted',
    variant = 'default',
    size = 'medium',
    overlay = true,
    className = ''
}) => {
    const messages = {
        default: {
            main: 'Generating',
            sub: 'Your intelligent report is being crafted'
        },
        medical: {
            main: 'Analyzing',
            sub: 'Processing medical data with AI precision'
        },
        analysis: {
            main: 'Analyzing',
            sub: 'Extracting insights from your data'
        },
        processing: {
            main: 'Processing',
            sub: 'Please wait while we handle your request'
        }
    };

    const currentMessage = messages[variant];
    const displayMessage = message || currentMessage.main;
    const displaySubMessage = subMessage || currentMessage.sub;

    const sizes = {
        small: {
            container: 'h-12 w-12',
            core: 'h-12 w-12',
            text: 'text-sm',
            subText: 'text-xs'
        },
        medium: {
            container: 'h-20 w-20',
            core: 'h-20 w-20',
            text: 'text-lg',
            subText: 'text-sm'
        },
        large: {
            container: 'h-28 w-28',
            core: 'h-28 w-28',
            text: 'text-xl',
            subText: 'text-base'
        }
    };

    const containerClass = overlay
        ? "absolute inset-0 z-50 flex flex-col items-center justify-center backdrop-blur-sm bg-[var(--bg-card)]/80"
        : "flex flex-col items-center justify-center p-8";

    return (
        <div className={`${containerClass} ${className}`}>
            {/* Enhanced animated pulse core with medical theme */}
            <div className="relative mb-6">
                {/* Outer pulsing ring */}
                <div className={`absolute inline-flex ${sizes[size].container} rounded-full bg-gradient-to-r from-[var(--primary-400)] via-[var(--success)] to-[var(--primary-500)] opacity-40 animate-ping`}></div>
                {/* Middle ring */}
                <div className={`absolute inline-flex ${sizes[size].container} rounded-full bg-gradient-to-r from-[var(--primary-500)] via-[var(--success)] to-[var(--primary-600)] opacity-60 animate-pulse`}></div>
                {/* Core */}
                <div className={`relative inline-flex ${sizes[size].core} rounded-full bg-gradient-to-r from-[var(--primary-600)] via-emerald-600 to-[var(--primary-700)] shadow-xl`}>
                    {/* Inner medical cross or AI symbol */}
                    <div className="absolute inset-0 flex items-center justify-center">
                        {variant === 'medical' ? (
                            <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M19 8h-2V6a2 2 0 00-2-2h-2V2a1 1 0 00-2 0v2H9a2 2 0 00-2 2v2H5a1 1 0 000 2h2v2a2 2 0 002 2h2v2a1 1 0 002 0v-2h2a2 2 0 002-2v-2h2a1 1 0 000-2zM15 12H9V6h6v6z" />
                            </svg>
                        ) : (
                            <svg className="w-6 h-6 text-white animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                            </svg>
                        )}
                    </div>
                </div>
            </div>

            {/* Enhanced typing animation with medical styling */}
            <div className={`text-center font-semibold text-[var(--text-heading)] ${sizes[size].text} flex items-center space-x-2`}>
                <span className="animate-fade-in-up bg-gradient-to-r from-[var(--primary-600)] to-emerald-600 bg-clip-text text-transparent">
                    {displayMessage}
                </span>
                <div className="flex space-x-1">
                    <span className="animate-bounce-dot animation-delay-0"></span>
                    <span className="animate-bounce-dot animation-delay-200"></span>
                    <span className="animate-bounce-dot animation-delay-400"></span>
                </div>
            </div>

            {/* Enhanced subtext */}
            <div className={`mt-3 text-[var(--text-secondary)] ${sizes[size].subText} text-center max-w-md leading-relaxed`}>
                {displaySubMessage}
            </div>

            {/* Progress indicator */}
            <div className="mt-4 w-48 bg-[var(--bg-hover)] rounded-full h-1.5 overflow-hidden">
                <div className="bg-gradient-to-r from-[var(--primary-500)] to-emerald-500 h-1.5 rounded-full animate-pulse"></div>
            </div>
        </div>
    );
};

export default AILoader;
